import { fetchOrLogout } from "../../../utils/helpers";
import { getBaseUrl } from "../../../utils/requestUtils";
import { TrackAnalyticsEvent } from "../../../services/Analytics";
import type { ChangedEditOrderPayload, EditOrderResult } from "../orderEdit";
import type { ReportUpdatePayload } from "../reportUpdate";
import type {
  NewReqClinicAccount,
  NewReqOrderingProvider,
  NewRequisitionType,
  OrderingProviderLookupResult,
  ShippingInformationRequest,
} from "../types";

interface OrderingProviderLookupResponse {
  lims_id: string;
  account_name: string;
  is_mobile_phlebotomy_account: boolean;
  providers?: NewReqOrderingProvider[];
}

export const submitNewRequisitionOrder = async (
  new_req_form: NewRequisitionType,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<NewRequisitionType | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/`,
      {
        method: "POST",
        body: JSON.stringify({ ...new_req_form }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = await response.json();
      return responseData;
    }
  } catch (e) {
    return undefined;
  }
};

const patchCorrection = async (
  path: string,
  payload: object,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<EditOrderResult> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}${path}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      return { ok: true, status: response.status };
    }
    const responseData = await response.json().catch(() => ({}));
    // DRF serializer errors arrive as a list of messages.
    return {
      ok: false,
      status: response.status,
      error: Array.isArray(responseData.error)
        ? responseData.error[0]
        : responseData.error,
      reason: responseData.reason,
    };
  } catch (e) {
    return { ok: false, status: 0 };
  }
};

export const editOrder = (
  unityCompleteId: string,
  editPayload: ChangedEditOrderPayload,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<EditOrderResult> =>
  patchCorrection(
    `/api/v1/provider_portal/electronic_orders/${unityCompleteId}/edit/`,
    editPayload,
    token,
    setLoggedOut,
  );

export const requestReportUpdate = (
  sfid: string,
  payload: ReportUpdatePayload,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<EditOrderResult> =>
  patchCorrection(
    `/api/v1/provider_portal/requisitions/${sfid}/report_update/`,
    payload,
    token,
    setLoggedOut,
  );

export const uploadNewReqImageForProcessing = async (
  file_data: string,
  token: string,
  preflight: boolean,
  type: "antigen" | "insurance",
  setLoggedOut: (inactivity: boolean) => void,
  insurance_type?: "secondary" | "primary",
  card_type?: "front" | "back",
): Promise<{ fileName: string; valid: boolean }> => {
  try {
    const baseUrl = await getBaseUrl();
    if (preflight) {
      return { fileName: "preflight" as string, valid: true };
    }
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/${
        type === "antigen"
          ? "upload_fetal_antigen_file"
          : "upload_insurance_card"
      }/`,
      {
        method: "POST",
        body: JSON.stringify({
          file_data,
          insurance_type: type === "insurance" ? insurance_type : undefined,
          card_type: type === "insurance" ? card_type : undefined,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = await response.json();
      return { fileName: responseData.file_name as string, valid: true };
    }
    if (response.status === 400) {
      const responseData = await response.json();
      return { fileName: responseData.error as string, valid: false };
    }
    return { fileName: "", valid: false };
  } catch (e) {
    return { fileName: "network error", valid: false };
  }
};

export const toBase64 = (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    (reader as FileReader).readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const GetAllNewReqLimsAccount = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<NewReqClinicAccount[]> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/lims_id_accounts/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = await response.json();
      return responseData as NewReqClinicAccount[];
    }
    return [];
  } catch (e) {
    return [];
  }
};

export const GetAllNewReqOrderingProviders = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<NewReqOrderingProvider[]> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/npi_contacts/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = await response.json();
      return responseData as NewReqOrderingProvider[];
    }
    return [];
  } catch (e) {
    return [];
  }
};

export const GetNewReqOrderingProvidersForLimsId = async (
  limsId: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<OrderingProviderLookupResult> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/npi_contacts/?lims_id=${encodeURIComponent(
        limsId,
      )}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData =
        (await response.json()) as OrderingProviderLookupResponse;
      return {
        limsId: responseData.lims_id,
        accountName: responseData.account_name,
        isMobilePhlebotomyAccount: responseData.is_mobile_phlebotomy_account,
        providers: responseData.providers ?? [],
      };
    }
    if (response.status === 404) {
      return { providers: [], error: "not_found" };
    }
    if (response.status === 400) {
      return { providers: [], error: "ambiguous" };
    }
    return { providers: [], error: "request_failed" };
  } catch (e) {
    return { providers: [], error: "request_failed" };
  }
};

export interface GooglePlacesAddress {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: {
    offset: number;
    value: string;
  }[];
}

export interface FormatedAddress {
  houseNumber: string;
  streetName: string;
  city: string;
  state: string;
  fullAddress: string;
  value: string;
  placeID: string;
  zip?: string;
}

export interface GoogleAddressDetails {
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
}

export const GetGooglePlacesAddress = async (
  input: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<FormatedAddress[]> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/address_lookup/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ lookup_input: input }),
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = (await response.json())
        .predictions as GooglePlacesAddress[];

      return responseData.map((address) => ({
        fullAddress: address.description,
        houseNumber: address.terms[0].value,
        streetName: address.terms[1].value,
        city: address.terms[2].value,
        state: address.terms[3].value,
        value: `${address.terms[0].value} ${address.terms[1].value}`,
        placeID: address.place_id,
      }));
    }
    return [];
  } catch (e) {
    return [];
  }
};

export const GetGooglePlacesAddressDetails = async (
  formattedAddress: FormatedAddress,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<FormatedAddress | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/address_detail_lookup/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ place_id: formattedAddress.placeID }),
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = (await response.json())
        .result as GoogleAddressDetails;
      const houseNumber =
        responseData.address_components.find((component) =>
          component.types.includes("street_number"),
        )?.long_name || formattedAddress.houseNumber;

      const streetName =
        responseData.address_components.find((component) =>
          component.types.includes("route"),
        )?.long_name || formattedAddress.streetName;
      const zip = responseData.address_components.find((component) =>
        component.types.includes("postal_code"),
      )?.long_name;

      const city =
        responseData.address_components.find(
          (component) =>
            component.types.includes("locality") ||
            component.types.includes("sublocality"),
        )?.long_name || formattedAddress.city;

      const state =
        responseData.address_components.find((component) =>
          component.types.includes("administrative_area_level_1"),
        )?.short_name || formattedAddress.state;

      return {
        ...formattedAddress,
        houseNumber,
        streetName,
        city,
        state,
        zip,
      };
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const getOnlineOrderPrintout = async (
  token: string,
  unityId: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<string> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/download/?unity_complete_id=${unityId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const pdf = await response.json();
      return pdf.printout_pdf_url as string;
    }
    return "";
  } catch (e) {
    return "";
  }
};

export interface GenerateTreatmentNotesNewOrderResponse {
  generated_notes_download_url: string;
}

export const generateTreatmentNotesNewOrder = async (
  treatment_notes: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<GenerateTreatmentNotesNewOrderResponse | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/generated_notes_download_url/`,
      {
        method: "POST",
        body: JSON.stringify({ treatment_notes }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const treatmeantURL = await response.json();
      return treatmeantURL as GenerateTreatmentNotesNewOrderResponse;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const submitDraftRequisitionOrder = async (
  new_req_form: NewRequisitionType,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<NewRequisitionType | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/save_draft/`,
      {
        method: "POST",
        body: JSON.stringify({ ...new_req_form }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = await response.json();
      return responseData;
    }
  } catch (e) {
    return undefined;
  }
};

export const deleteDraftRequisitionOrder = async (
  unity_complete_id: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<void> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/delete_draft/`,
      {
        method: "DELETE",
        body: JSON.stringify({ unity_complete_id }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      const responseData = await response.json();
      return responseData;
    }
  } catch (e) {
    return undefined;
  }
};

export const shipKitToPatient = async (
  unity_complete_id: string,
  is_mobile_phlebotomy: boolean,
  shipping_information: ShippingInformationRequest,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<boolean> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/${unity_complete_id}/`,
      {
        method: "PATCH",
        body: JSON.stringify({
          is_mobile_phlebotomy,
          shipping_information,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      await TrackAnalyticsEvent("ProviderShipKitToPatient", token, undefined, {
        unity_complete_id,
      });
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

export const updatePhlebotomyStatus = async (
  unity_complete_id: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<boolean> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/electronic_orders/${unity_complete_id}/`,
      {
        method: "PATCH",
        body: JSON.stringify({
          is_mobile_phlebotomy: true,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (response.ok) {
      await TrackAnalyticsEvent(
        "ProviderOnlineOrderPhlebotomyTable",
        token,
        undefined,
        { unity_complete_id },
      );
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};
